<template>
  <b-modal size="lg" v-model="showForm" :title="$t('modalTitle')" no-close-on-backdrop no-enforce-focus>
    <!-- Trạng thái KYC (info only) -->
    <div v-if="formModel.confirmTime" class="alert alert-success py-2 mb-3">
      <i class="fa fa-check-circle mr-1"></i>
      <strong>{{ $t('kycConfirmed') }}</strong>
      — {{ formModel.confirmTime | moment('DD/MM/YY HH:mm') }}
    </div>
    <div v-else-if="formModel.cancelTime" class="alert alert-danger py-2 mb-3">
      <i class="fa fa-times-circle mr-1"></i>
      <strong>{{ $t('kycRejected') }}</strong>
      — {{ formModel.cancelTime | moment('DD/MM/YY HH:mm') }}
    </div>
    <div v-else class="alert alert-warning py-2 mb-3">
      <i class="fa fa-clock mr-1"></i>
      <strong>{{ $t('kycWaiting') }}</strong>
    </div>

    <!-- Thông tin chung -->
    <fieldset class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-info-circle mr-1"></i>{{ $t('clientInfo') }}</legend>
      <dl class="row mb-0">
        <dt class="col-sm-4 text-muted">{{ $t('client') }}</dt>
        <dd class="col-sm-8">
          <b v-if="formModel.client">{{ formModel.client.companyName }}</b>
          <code v-if="formModel.client" class="ml-2">{{ formModel.client.domain }}</code>
        </dd>
        <dt class="col-sm-4 text-muted">{{ $t('customer') }}</dt>
        <dd class="col-sm-8">
          <span :class="formModel.type === 'enterprise' ? 'badge badge-primary' : 'badge badge-secondary'">
            {{ $t(formModel.type) }}
          </span>
        </dd>
      </dl>
    </fieldset>

    <!-- Hợp đồng đã upload trước (nếu có) -->
    <fieldset v-if="formModel.contract && formModel.contractUrl" class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-file-signature mr-1"></i>{{ $t('currentContract') }}</legend>
      <a :href="formModel.contractUrl" target="_blank" class="btn btn-outline-success is-border-1">
        <i class="fa fa-download mr-1"></i>{{ $t('downloadCurrent') }}
      </a>
      <small class="text-muted ml-2" v-if="formModel.sendContractTime">
        ({{ $t('uploadedAt') }} {{ formModel.sendContractTime | moment('DD/MM/YY HH:mm') }})
      </small>
    </fieldset>

    <!-- Form upload + note -->
    <fieldset class="kyc-section">
      <legend class="kyc-legend">
        <i class="fa fa-upload mr-1"></i>{{ formModel.contract ? $t('replaceContract') : $t('uploadContract') }}
      </legend>
      <div class="form-group">
        <label class="font-weight-bold mb-1">{{ $t('contractFile') }} <span class="text-danger">*</span></label>
        <small class="form-text text-muted mb-2">{{ $t('contractFileHint') }}</small>
        <form-file service="/upload/upload-kyc" :model="formModel" attribute="contract" :errors="formErrors"
                   :accept="'image/jpeg,image/jpg,application/pdf'">
        </form-file>
      </div>
      <div class="form-group mb-0">
        <label class="font-weight-bold mb-1">{{ $t('note') }}</label>
        <small class="form-text text-muted mb-2">{{ $t('noteHint') }}</small>
        <form-textarea :rows="3" attribute="note" :model="formModel" :errors="formErrors" :placeholder="$t('notePlaceholder')" />
      </div>
    </fieldset>

    <template v-slot:modal-footer>
      <div class="d-flex w-100 justify-content-between align-items-center">
        <button class="btn btn-secondary" @click="showForm = false">
          {{ $t('close') }}
        </button>
        <div>
          <button
            v-if="formModel._id && formModel.type && ['owner', 'admin', 'tech', 'operationsManager', 'account', 'accountant', 'sales'].includes($store.state.user.role)"
            class="btn btn-success mr-2"
            @click="$refs.exportContract.open(formModel._id)">
            <i class="fa fa-file-pdf mr-1"></i>{{ $t('exportContract') }}
          </button>
          <button v-if="!['accountant'].includes($store.state.user.role)"
                  class="btn btn-primary" @click="onSave"
                  :disabled="!formModel.contract || processing">
            <i class="fa fa-save mr-1"></i>{{ $t('saveBtn') }}
          </button>
        </div>
      </div>
    </template>
    <export-contract-modal ref="exportContract"></export-contract-modal>
  </b-modal>
</template>

<script>
import CRUD from "../../../mixins/CRUD";
import ExportContractModal from "./ExportContractModal";

export default {
  mixins: [CRUD],
  components: {
    ExportContractModal,
  },
  data: function () {
    return {
      service: '/client-kyc',
      reloadBus: 'reloadKYC',
      showForm: false,
      processing: false,
    }
  },
  methods: {
    open(item) {
      this.formModel = item;
      this.showForm = true;
    },
    onSave() {
      if (!this.formModel.contract) {
        this.$swal({title: this.$t('contractRequired'), icon: 'warning'});
        return;
      }
      this.$swal({
        title: this.$t('saveConfirmTitle'),
        text: this.$t('saveConfirmText'),
        icon: 'question',
        showCancelButton: true,
      }).then(result => {
        if (!result.value) return;
        this.processing = true;
        const loader = this.$loading.show();
        const url = `${this.service}/send-contract/${this.formModel._id}`;
        this.$service.put(url, this.formModel).then(response => {
          if (response && response.data) {
            this.$swal({
              title: this.$t('success'),
              text: this.$t('saveSuccess'),
              icon: 'success',
              toast: true,
              timer: 4000,
              position: 'top-end',
            });
            this.showForm = false;
            if (this.reloadBus) this.$bus.$emit(this.reloadBus);
          }
        }).catch(error => {
          if (error.response && error.response.data && error.response.data.message) {
            this.$swal({text: error.response.data.message, icon: 'error'});
          } else {
            this.formErrors = (error.response && error.response.data) || [];
          }
        }).finally(() => {
          this.processing = false;
          loader.hide();
        });
      });
    },
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Signed contract management',
        clientInfo: 'Client info',
        currentContract: 'Current uploaded contract',
        downloadCurrent: 'Download current file',
        uploadedAt: 'uploaded at',
        uploadContract: 'Upload signed contract',
        replaceContract: 'Replace signed contract',
        contractFile: 'Signed contract file',
        contractFileHint: 'Upload PDF or JPG of the contract signed by customer (max 10MB)',
        contractRequired: 'Please select a file before saving',
        note: 'Note',
        noteHint: 'Optional note about this contract',
        notePlaceholder: 'E.g. signed via post on 06/05/2026',
        kycConfirmed: 'KYC profile approved',
        kycRejected: 'KYC profile has been rejected',
        kycWaiting: 'KYC profile waiting for review',
        close: 'Close',
        saveBtn: 'Save',
        success: 'Success',
        saveConfirmTitle: 'Save signed contract?',
        saveConfirmText: 'The file will be saved to KYC. The customer will not be notified.',
        saveSuccess: 'Contract file saved',
        client: 'Client',
        customer: 'Type',
        personal: 'Personal',
        enterprise: 'Enterprise',
        exportContract: 'Export PDF',
      },
      vi: {
        modalTitle: 'Hợp đồng đã ký',
        clientInfo: 'Thông tin khách',
        currentContract: 'File hợp đồng đang lưu',
        downloadCurrent: 'Tải file hiện tại',
        uploadedAt: 'tải lên lúc',
        uploadContract: 'Tải lên hợp đồng đã ký',
        replaceContract: 'Thay thế hợp đồng đã ký',
        contractFile: 'File hợp đồng đã ký',
        contractFileHint: 'Upload file PDF/JPG do khách ký (tối đa 10MB)',
        contractRequired: 'Vui lòng chọn file trước khi lưu',
        note: 'Ghi chú',
        noteHint: 'Ghi chú thêm (tuỳ chọn)',
        notePlaceholder: 'VD: ký qua đường bưu điện ngày 06/05/2026',
        kycConfirmed: 'Hồ sơ KYC đã được duyệt',
        kycRejected: 'Hồ sơ KYC đã bị từ chối',
        kycWaiting: 'Hồ sơ KYC đang chờ duyệt',
        close: 'Đóng',
        saveBtn: 'Lưu',
        success: 'Thành công',
        saveConfirmTitle: 'Lưu file hợp đồng đã ký?',
        saveConfirmText: 'File sẽ được lưu vào KYC. Khách hàng KHÔNG được thông báo.',
        saveSuccess: 'Đã lưu file hợp đồng',
        client: 'Khách',
        customer: 'Loại',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
        exportContract: 'Xuất PDF',
      }
    }
  }
}
</script>

<style scoped>
.kyc-section {
  border: 1px solid #e3e6f0;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fff;
}

.kyc-legend {
  width: auto;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #5a5c69;
  padding: 2px 8px;
  margin-bottom: 8px;
  background: #f8f9fc;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
}

.kyc-section dl.row dt {
  font-weight: 500;
  margin-bottom: 6px;
}

.kyc-section dl.row dd {
  margin-bottom: 6px;
  word-break: break-word;
}
</style>
